import {initializeApp} from "firebase/app";
import {
	browserLocalPersistence,
	connectAuthEmulator,
	indexedDBLocalPersistence,
	initializeAuth
} from "firebase/auth";
import {connectStorageEmulator, getStorage} from 'firebase/storage';
import {
	clearIndexedDbPersistence,
	connectFirestoreEmulator,
	getFirestore,
	initializeFirestore,
	persistentLocalCache,
	terminate,
} from 'firebase/firestore';
import {connectFunctionsEmulator, getFunctions} from 'firebase/functions';
import {initializeFirebaseAppCheck} from './firebaseAppCheck';

const appCheckSiteKey = process.env.REACT_APP_FIREBASE_APP_CHECK_SITE_KEY;

const measurementId = process.env.REACT_APP_FIREBASE_MEASUREMENT_ID;
const firebaseConfig = {
	apiKey: process.env.REACT_APP_FIREBASE_API_KEY ?? "REPLACE_WITH_FIREBASE_API_KEY",
	authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ?? "REPLACE_WITH_FIREBASE_AUTH_DOMAIN",
	projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID ?? "REPLACE_WITH_FIREBASE_PROJECT_ID",
	storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET ?? "REPLACE_WITH_FIREBASE_STORAGE_BUCKET",
	messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID ?? "REPLACE_WITH_FIREBASE_MESSAGING_SENDER_ID",
	appId: process.env.REACT_APP_FIREBASE_APP_ID ?? "REPLACE_WITH_FIREBASE_APP_ID",
	...(measurementId ? {measurementId} : {})
};

// Resolve emulator flag before initializing Firestore so the correct cache
// strategy is applied from the start (initializeFirestore must be called once).
const appEnv = process.env.REACT_APP_ENV ?? 'localTest';
const emulatorExplicitOff = process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'false';
export const shouldUseEmulator = process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true'
	|| (
		process.env.NODE_ENV !== 'production'
		&& appEnv === 'localTest'
		&& !emulatorExplicitOff
	);

const app = initializeApp(firebaseConfig);
// Email-link auth only — skip popup/redirect resolver so gapi (apis.google.com) is not loaded.
export const auth = initializeAuth(app, {
	persistence: [indexedDBLocalPersistence, browserLocalPersistence]
});

// Use IndexedDB persistent cache in production for instant repeat loads.
// The emulator keeps the default memory cache to avoid stale state between test runs.
export const db = shouldUseEmulator
	? getFirestore(app)
	: initializeFirestore(app, {localCache: persistentLocalCache()});

export const storage = getStorage(app);
const functionsRegion = process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION ?? 'europe-west1';
export const functions = getFunctions(app, functionsRegion);

const initAppCheck = async () => {
	await initializeFirebaseAppCheck(app, {
		appCheckSiteKey,
		shouldUseEmulator,
		onError: (message) => {
			console.warn(
				'Firebase App Check initialization failed:',
				message,
				'\nVerify: reCAPTCHA site key format, Firebase Console App Check registration, and that PROD_REACT_APP_FIREBASE_APP_CHECK_SITE_KEY is set correctly in GitLab CI.',
				'\nSee README → "Troubleshooting App Check 403".'
			);
		},
		onTokenError: (error) => {
			const code = error?.code ?? '';
			if (code === 'appCheck/initial-throttle') {
				console.warn(
					'Firebase App Check throttled (appCheck/initial-throttle). The SDK will retry after ~24 h.',
					'\nFix: confirm the reCAPTCHA domain allowlist in Google reCAPTCHA Admin includes linkspace.tlvt.org,',
					'verify PROD_REACT_APP_FIREBASE_APP_CHECK_SITE_KEY matches the Firebase Console App Check provider,',
					'then redeploy and test in a fresh browser profile.',
					'\nSee README → "Troubleshooting App Check 403".'
				);
			} else {
				console.warn(
					'Firebase App Check token error:',
					code || error?.message,
					'\nSee README → "Troubleshooting App Check 403".'
				);
			}
		}
	});
};

/**
 * Clears the Firestore IndexedDB cache for the current user.
 * Must be called after all listeners have been unsubscribed and before the
 * next Firestore operation (i.e. at logout). Terminates the Firestore client
 * and reloads the page so the next user starts with a clean instance.
 */
export const clearFirestoreCache = async () => {
	if (shouldUseEmulator) return;
	try {
		await terminate(db);
		await clearIndexedDbPersistence(db);
	} catch {
		// Ignore — cache clear is best-effort; the page reload follows regardless.
	}
	globalThis.window?.location.reload();
};

if (shouldUseEmulator) {
	const firestoreHost = process.env.FIRESTORE_EMULATOR_HOST ?? '127.0.0.1:28080';
	const authHost = process.env.FIREBASE_AUTH_EMULATOR_HOST ?? '127.0.0.1:29099';
	const storageHost = process.env.FIREBASE_STORAGE_EMULATOR_HOST ?? '127.0.0.1:29199';

	const [firestoreHostname, firestorePort] = firestoreHost.split(':');
	const [authHostname, authPort] = authHost.split(':');
	const [storageHostname, storagePort] = storageHost.split(':');

	const functionsHost = process.env.FIREBASE_FUNCTIONS_EMULATOR_HOST ?? '127.0.0.1:5001';
	const [functionsHostname, functionsPort] = functionsHost.split(':');

	connectFirestoreEmulator(db, firestoreHostname, Number(firestorePort));
	connectAuthEmulator(auth, `http://${authHostname}:${authPort}`);
	connectStorageEmulator(storage, storageHostname, Number(storagePort));
	connectFunctionsEmulator(functions, functionsHostname, Number(functionsPort));
} else {
	void initAppCheck();
}
